import { type Dictionary, type Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { CampusArea } from '@chancenwerk/campus-learning-content/entities/CampusSection.js';

@Entity()
export class CampusSectionFilter extends AdornisFilter {
  static override _class = 'CampusSectionFilter';

  constructor(params: Partial<CampusSectionFilter>) {
    super(params);
  }

  @Field(type => Boolean, {
    default: v => v ?? null,
  })
  hiddenFromStudents: Maybe<boolean>;

  @Field(type => String, {
    default: v => v ?? '',
  })
  userSearchText?: string;

  @Field(type => String, {
    default: v => v ?? CampusArea.STUDENTS,
  })
  area?: string;

  @Field(type => [String])
  userCheckedSubjects?: string[];

  @Field(type => [String])
  userHideSubjects?: string[];

  @Field(type => [Int])
  userCheckedGrades?: number[];

  @Field(type => [String])
  lessonIDs?: string[];

  @Field(type => Int)
  userIsGrade?: number;

  @Field(type => String, {
    default: v => v ?? '',
  })
  userID?: string;

  @Field(type => Boolean, {
    default: v => v ?? false,
  })
  showAssignedLessons?: boolean;

  @Field(type => Boolean, {
    default: v => v ?? false,
  })
  playMode?: boolean;

  override getMongoFilter() {
    const selector: Dictionary<any> = {};
    const obj = this.toObject();

    if (obj.area) {
      selector.area = obj.area;
    }

    if (obj.userCheckedGrades && obj.userCheckedGrades?.length > 0) {
      selector.forGrades = { $all: Array.from(obj.userCheckedGrades) };
    }

    if (obj.userCheckedSubjects && obj.userCheckedSubjects?.length > 0) {
      if (obj.userHideSubjects && obj.userHideSubjects?.length > 0) {
        // Both conditions are present, use $and to combine them
        selector.$and = [
          { subjects: { $in: Array.from(obj.userCheckedSubjects) } },
          { subjects: { $nin: Array.from(obj.userHideSubjects) } },
        ];
      } else {
        // Only userCheckedSubjects is present
        selector.subjects = { $in: Array.from(obj.userCheckedSubjects) };
      }
    } else if (obj.userHideSubjects && obj.userHideSubjects?.length > 0) {
      // Only userHideSubjects is present
      selector.subjects = { $nin: Array.from(obj.userHideSubjects) };
    }

    if (obj.playMode && obj.userIsGrade && obj.userIsGrade > 0 && !obj.showAssignedLessons) {
      selector.forGrades = obj.userIsGrade;
    }

    if (obj.playMode) {
      selector.hiddenFromStudents = false;
    }

    if (obj.hiddenFromStudents === true) {
      selector.hiddenFromStudents = true;
    } else if (obj.hiddenFromStudents === false) {
      selector.hiddenFromStudents = { $ne: true };
    }

    if (this.search) {
      selector.name = { $regex: new RegExp(this.search, 'i') };
    }

    if (this.lessonIDs) {
      selector.lessonIDs = { $in: this.lessonIDs };
    }

    return selector;
  }
}
